import type { FocusEvent, MouseEvent } from 'react';
import React from 'react';
import { cloneObjectWithPrefixedProperties, getProductImageURL } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { FREE_UK_DELIVERY } from '../constants/copy';
import * as styles from '../styles';
import { PrimaryLabel } from 'client-ribbons-design-system/components/elements/Labels/PrimaryLabel/PrimaryLabel';
import { Price } from 'client-ribbons-design-system/components/elements/Price/Price';
import { IconLocalShippingLocal } from 'client-ribbons-design-system/components/foundations/Iconography/IconLocalShippingLocal';
import { Image } from 'client-ribbons-design-system/components/foundations/Image/Image';
import * as imageStyles from 'client-ribbons-design-system/styles/image';

export const CAROUSEL_PRODUCT_PRICE = 'carousel-product-price';
export const CAROUSEL_PRODUCT_PREVIOUS_PRICE = 'carousel-product-price';

export interface CarouselProductCardProps {
  alt: string;
  currency: CurrencyCode;
  enableFreeDomesticDeliveryLabels?: boolean;
  freeDomesticDelivery?: boolean;
  hideLabelIcons?: boolean;
  href: string;
  imageLoading?: 'eager' | 'lazy';
  isNew: boolean;
  linkDataAttributes?: Record<string, string>;
  onBlur?: (e: FocusEvent<HTMLAnchorElement>) => void;
  onFocus?: (e: FocusEvent<HTMLAnchorElement>) => void;
  onLinkClick?: (evt: MouseEvent<HTMLAnchorElement>) => void;
  preSalePrice?: number;
  price: number;
  purchasable: boolean;
  salePercentage?: number;
  size?: 'regular' | 'auto';
  src: string;
  title: string;
}

export const CarouselProductCard = ({
  alt,
  currency,
  enableFreeDomesticDeliveryLabels = false,
  freeDomesticDelivery,
  hideLabelIcons = false,
  href,
  imageLoading = 'lazy',
  isNew,
  linkDataAttributes = {},
  onBlur,
  onFocus,
  onLinkClick = () => {},
  preSalePrice,
  price,
  purchasable,
  salePercentage = 0,
  size = 'regular',
  src,
  title,
}: CarouselProductCardProps) => {
  const linkDataProps = cloneObjectWithPrefixedProperties(linkDataAttributes, 'data-');

  return (
    <a
      css={[imageStyles.imageWidth[size], styles.product]}
      href={href}
      onBlur={onBlur}
      onClick={onLinkClick}
      onFocus={onFocus}
      {...linkDataProps}
    >
      <h3 css={styles.productHeading}>
        <Image
          alt={alt}
          css={[imageStyles.image[size], styles.fitImageToContainer]}
          loading={imageLoading}
          src={getProductImageURL(src, 'normal')}
        />
        <p css={styles.productTitle}>{title}</p>
      </h3>
      <PrimaryLabel
        className="similar-product-image__label"
        css={styles.productLabel}
        hasStock={purchasable}
        isNew={isNew}
        salePercentage={salePercentage ?? undefined}
      />
      <div css={styles.productPrices}>
        {price && (
          <Price
            amount={price}
            css={styles.productPrice}
            currency={currency}
            testId={CAROUSEL_PRODUCT_PRICE}
            type={preSalePrice !== undefined ? 'sale' : 'normal'}
          />
        )}
        {preSalePrice && (
          <Price
            amount={preSalePrice}
            css={styles.productPreviousPrice}
            currency={currency}
            testId={CAROUSEL_PRODUCT_PREVIOUS_PRICE}
            type="previous"
          />
        )}
      </div>
      {enableFreeDomesticDeliveryLabels && freeDomesticDelivery && (
        <div css={styles.freeDeliveryLabelWrapper}>
          <span css={styles.freeDelivery}>
            {!hideLabelIcons && (
              <IconLocalShippingLocal css={styles.freeDeliveryIcon} height={16} width={16} />
            )}
            {FREE_UK_DELIVERY}
          </span>
        </div>
      )}
    </a>
  );
};
